/* CompanyOverviewPage — shows all projects for a company as clickable blocks
 * grouped by tool, with progress / status / last updated. Replaces the
 * old auto-redirect-to-first-project behaviour.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatError } from "../utils/api";
import Navbar from "../components/Navbar";
import {
  ChevronLeft, Plus, Users, GitBranch, CheckSquare, Zap, Columns, MessageSquare, Timer, MapPin, GitFork, BarChart2, TrendingUp, Folder,
  Spline, ShieldAlert, FileSpreadsheet, Workflow, BarChart3, LayoutGrid,
  Waves, Calculator, Inbox, ListChecks, Shield, Trello, Activity, Calendar, ClipboardCheck, DollarSign, Grid3x3, GraduationCap, Users2, Award, Phone, Sigma, TrendingDown, Bot, Map, Clock,
  Share2 as PortalIcon, Copy as CopyIcon, X as CloseIcon, Check, Lock, Lightbulb,
} from "lucide-react";

const TOOL_META = {
  staffing_assessment: { name: "Staffing Diagnostic",       icon: Users,         steps: 8, accent: "#3B82F6" },
  vsm_builder:         { name: "Value Stream Map",          icon: GitBranch,     steps: 5, accent: "#D97706" },
  sipoc_mapper:        { name: "SIPOC",                     icon: Columns,       steps: 3, accent: "#6366F1" },
  voc_kano:            { name: "VoC + Kano",                icon: MessageSquare, steps: 3, accent: "#E11D48" },
  time_study:          { name: "Time Study",                icon: Timer,         steps: 3, accent: "#0891B2" },
  waste_walk:          { name: "Waste Walk",                icon: MapPin,        steps: 3, accent: "#EA580C" },
  rca_5whys_fishbone:  { name: "5 Whys + Fishbone",         icon: GitFork,       steps: 4, accent: "#0D9488" },
  pareto_analyzer:     { name: "Pareto",                    icon: BarChart2,     steps: 3, accent: "#CA8A04" },
  spaghetti_diagram:   { name: "Spaghetti Diagram",         icon: Spline,        steps: 3, accent: "#B45309" },
  pfmea:               { name: "PFMEA",                     icon: ShieldAlert,   steps: 3, accent: "#B91C1C" },
  a3_problem_solving:  { name: "A3 Problem Solving",        icon: FileSpreadsheet, steps: 3, accent: "#475569" },
  dmaic_charter:       { name: "DMAIC Charter",             icon: Workflow,      steps: 3, accent: "#1D4ED8" },
  yamazumi:            { name: "Yamazumi",                  icon: BarChart3,     steps: 3, accent: "#C026D3" },
  impact_effort:       { name: "Impact × Effort",           icon: LayoutGrid,    steps: 3, accent: "#65A30D" },
  five_s_audit:        { name: "5S Audit",                  icon: CheckSquare,   steps: 7, accent: "#A855F7" },
  kaizen_charter:      { name: "Kaizen Charter",            icon: Zap,           steps: 5, accent: "#16A34A" },
  maturity_assessment: { name: "Maturity Assessment",       icon: TrendingUp,    steps: 3, accent: "#059669" },
  // C — Implement
  lean_wave_charter:   { name: "Lean Wave Charter",         icon: Waves,         steps: 3, accent: "#0EA5E9" },
  roi_calculator:      { name: "Benefit / ROI",             icon: Calculator,    steps: 3, accent: "#15803D" },
  idea_bank:           { name: "Idea Bank",                 icon: Inbox,         steps: 3, accent: "#7C3AED" },
  standard_work:       { name: "Standard Work",             icon: ListChecks,    steps: 3, accent: "#2563EB" },
  poka_yoke:           { name: "Poka-Yoke",                 icon: Shield,        steps: 3, accent: "#DC2626" },
  kanban_designer:     { name: "Kanban / Pull",             icon: Trello,        steps: 3, accent: "#0891B2" },
  smed:                { name: "SMED",                      icon: Timer,         steps: 3, accent: "#9333EA" },
  // D — Sustain
  kpi_scorecard:       { name: "KPI Scorecard",             icon: Activity,      steps: 3, accent: "#BE185D" },
  tier_meeting:        { name: "Tier Meetings",             icon: Users,         steps: 3, accent: "#1E40AF" },
  leader_standard_work:{ name: "Leader Std Work",           icon: Calendar,      steps: 3, accent: "#0F766E" },
  lpa_scheduler:       { name: "LPA Scheduler",             icon: ClipboardCheck,steps: 3, accent: "#92400E" },
  benefit_tracker:     { name: "Benefit Tracker",           icon: DollarSign,    steps: 3, accent: "#15803D" },
  // E — People
  iluo_matrix:         { name: "ILUO Matrix",               icon: Grid3x3,       steps: 3, accent: "#6D28D9" },
  tna:                 { name: "TNA",                       icon: GraduationCap, steps: 3, accent: "#0E7490" },
  adkar:               { name: "ADKAR",                     icon: Users2,        steps: 3, accent: "#B91C1C" },
  lean_academy:        { name: "Lean Academy",              icon: Award,         steps: 3, accent: "#A16207" },
  // F — Domain
  erlang_c:            { name: "Erlang C",                  icon: Phone,         steps: 2, accent: "#7E22CE" },
  monte_carlo_queue:   { name: "Monte Carlo Queue",         icon: Sigma,         steps: 2, accent: "#1E3A8A" },
  attrition_cohort:    { name: "Attrition Cohort",          icon: TrendingDown,  steps: 3, accent: "#9F1239" },
  automation_scorer:   { name: "Automation Scorer",         icon: Bot,           steps: 3, accent: "#1F2937" },
  customer_journey:    { name: "Customer Journey",          icon: Map,           steps: 3, accent: "#BE123C" },
  // DILO
  dilo:                { name: "DILO",                      icon: Clock,         steps: 3, accent: "#0F766E" },
};

const TOOL_GROUPS = [
  { label: "Diagnose",  ids: ["voc_kano", "sipoc_mapper", "dilo", "vsm_builder", "staffing_assessment", "time_study", "waste_walk", "rca_5whys_fishbone", "pareto_analyzer"] },
  { label: "Design",    ids: ["spaghetti_diagram", "pfmea", "a3_problem_solving", "dmaic_charter", "yamazumi", "impact_effort", "kaizen_charter"] },
  { label: "Implement", ids: ["lean_wave_charter", "roi_calculator", "idea_bank", "standard_work", "poka_yoke", "kanban_designer", "smed"] },
  { label: "Sustain",   ids: ["kpi_scorecard", "tier_meeting", "leader_standard_work", "lpa_scheduler", "benefit_tracker", "five_s_audit", "maturity_assessment"] },
  { label: "People",    ids: ["iluo_matrix", "tna", "adkar", "lean_academy"] },
  { label: "Domain",    ids: ["erlang_c", "monte_carlo_queue", "attrition_cohort", "automation_scorer", "customer_journey"] },
];

const JOURNEY_ORDER = [
  "voc_kano", "sipoc_mapper", "dilo", "vsm_builder", "staffing_assessment", "time_study", "waste_walk", "spaghetti_diagram",
  "pareto_analyzer", "rca_5whys_fishbone", "pfmea", "a3_problem_solving", "impact_effort",
  "yamazumi", "standard_work", "poka_yoke", "kanban_designer", "smed", "dmaic_charter", "kaizen_charter",
  "roi_calculator", "lean_wave_charter", "idea_bank",
  "kpi_scorecard", "tier_meeting", "leader_standard_work", "lpa_scheduler", "five_s_audit",
  "iluo_matrix", "tna", "adkar", "lean_academy",
  "benefit_tracker", "maturity_assessment",
  "erlang_c", "monte_carlo_queue", "attrition_cohort", "automation_scorer", "customer_journey",
];

function relTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function CrossToolInsightPanel({ projects }) {
  // Build live data: pull pain-points from VSM, top Pareto categories, Improve-phase actions etc.
  const byTool = projects.reduce((acc, p) => { (acc[p.tool_type] = acc[p.tool_type] || []).push(p); return acc; }, {});
  const insights = [];

  // VSM pain-points → suggest 5-Whys / Fishbone if not yet done
  const vsmProjs = byTool.vsm_builder || [];
  const vsmPainCount = vsmProjs.reduce((acc, p) => {
    const fromCanvas = (p.tool_data?.current_flow?.nodes || []).reduce((a, n) => a + (n.data?.painpoints?.length || 0), 0);
    const fromFlat = Array.isArray(p.tool_data?.pain_points) ? p.tool_data.pain_points.length : 0;
    const fromAlt = Array.isArray(p.tool_data?.painpoints) ? p.tool_data.painpoints.length : 0;
    return acc + fromCanvas + fromFlat + fromAlt;
  }, 0);
  if (vsmPainCount > 0 && !(byTool.rca_5whys_fishbone || []).length) {
    insights.push({ severity: "high", icon: "💡", title: `${vsmPainCount} VSM pain-points captured but no 5-Whys / Fishbone yet`, action: "Promote them to a root-cause analysis", suggestNext: "rca_5whys_fishbone" });
  }
  // Pareto vital-few → suggest PFMEA
  const paretoProjs = byTool.pareto_analyzer || [];
  const vitalFew = paretoProjs.flatMap(p => (p.tool_data?.categories || [])).length;
  if (vitalFew > 0 && !(byTool.pfmea || []).length) {
    insights.push({ severity: "medium", icon: "🎯", title: `Pareto identifies vital-few defects, but no PFMEA covers them`, action: "Run a PFMEA on the top categories", suggestNext: "pfmea" });
  }
  // DMAIC Improve actions → suggest Impact × Effort if missing
  const dmaicProjs = byTool.dmaic_charter || [];
  const hasImproveDeliverables = dmaicProjs.some(p => (p.tool_data?.phases?.improve?.deliverables || []).length > 0);
  if (hasImproveDeliverables && !(byTool.impact_effort || []).length) {
    insights.push({ severity: "medium", icon: "🧮", title: `DMAIC Improve-phase actions exist but haven't been Impact×Effort scored`, action: "Triage them in the 2×2 matrix", suggestNext: "impact_effort" });
  }
  // VSM done but no Time Study
  if ((byTool.vsm_builder || []).length > 0 && !(byTool.time_study || []).length) {
    insights.push({ severity: "low", icon: "⏱", title: "VSM is mapped but no Time Study has captured cycle-time data", action: "Validate process times with a Time Study", suggestNext: "time_study" });
  }
  // 5S audit but no LPA scheduled
  if ((byTool.five_s_audit || []).length > 0 && !(byTool.lpa_scheduler || []).length) {
    insights.push({ severity: "low", icon: "📋", title: "5S audit done — but no Layered Process Audit programme scheduled", action: "Schedule LPAs to sustain the gains", suggestNext: "lpa_scheduler" });
  }
  // ROI without Benefit Tracker
  if ((byTool.roi_calculator || []).length > 0 && !(byTool.benefit_tracker || []).length) {
    insights.push({ severity: "medium", icon: "💰", title: "ROI cases authored but no actuals being tracked yet", action: "Stand up a Benefit Realisation Tracker", suggestNext: "benefit_tracker" });
  }
  // Kaizen done but no Standard Work follow-up
  if ((byTool.kaizen_charter || []).length > 0 && !(byTool.standard_work || []).length) {
    insights.push({ severity: "low", icon: "📐", title: "Kaizen events run — codify gains in Standard Work?", action: "Generate the SOP", suggestNext: "standard_work" });
  }

  if (insights.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-6 flex items-center gap-2 text-xs text-green-800" data-testid="cross-tool-insight-empty">
        <Lightbulb className="w-4 h-4" /> No cross-tool gaps detected — your toolbox is well-connected.
      </div>
    );
  }
  const sev = { high: "border-red-300 bg-red-50", medium: "border-amber-300 bg-amber-50", low: "border-sage-300 bg-sage-50" };
  return (
    <div className="mb-6" data-testid="cross-tool-insight">
      <div className="flex items-center gap-2 mb-2">
        <Lightbulb className="w-4 h-4 text-amber-600" />
        <h3 className="font-heading text-sm font-semibold text-sage-800">Cross-Tool Insights</h3>
        <span className="text-xs text-sage-500">— connections we noticed across this company's tools</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {insights.map((it, i) => (
          <div key={i} className={`rounded-md p-3 border-l-4 ${sev[it.severity]} flex items-start gap-2`} data-testid={`insight-${i}`}>
            <span className="text-lg">{it.icon}</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-sage-900">{it.title}</p>
              <p className="text-xs text-sage-600">{it.action}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WorkflowJourney({ projects, tools, companyId, onPickTool }) {
  const projByTool = projects.reduce((acc, p) => { (acc[p.tool_type] = acc[p.tool_type] || []).push(p); return acc; }, {});
  const unlocked = new Set(tools.filter(t => t.unlocked).map(t => t.id));
  // Suggest = first journey-order tool the user has unlocked but never used
  const suggestion = JOURNEY_ORDER.find(id => unlocked.has(id) && !projByTool[id]);
  return (
    <div className="bg-white border border-sage-200 rounded-md p-4 mb-6" data-testid="workflow-journey">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-sage-500">Workflow Journey</p>
          <p className="text-sm text-sage-700">Diagnose → Design → Sustain · {Object.keys(projByTool).length} of {JOURNEY_ORDER.length} tools used</p>
        </div>
        {suggestion && TOOL_META[suggestion] && (
          <button data-testid="journey-suggest-cta" onClick={() => onPickTool(suggestion)} className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-amber-100 flex items-center gap-1.5">
            <Lightbulb className="w-3.5 h-3.5" /> Suggested next: {TOOL_META[suggestion].name}
          </button>
        )}
      </div>
      <div className="overflow-x-auto -mx-2 px-2">
        <div className="flex items-stretch gap-1.5 min-w-min" data-testid="journey-track">
          {JOURNEY_ORDER.map((id) => {
            const m = TOOL_META[id]; if (!m) return null;
            const Icon = m.icon;
            const projs = projByTool[id] || [];
            const hasProj = projs.length > 0;
            const isComplete = projs.some(p => (p.wizard_progress || 1) >= m.steps);
            const isLocked = !unlocked.has(id);
            const isSuggested = id === suggestion;
            const onClick = () => {
              if (isLocked) return;
              if (hasProj) {
                const proj = projs[0];
                const path = proj.tool_type === "staffing_assessment" ? `/workspace/${companyId}/project/${proj.id}` : `/tool/${companyId}/project/${proj.id}`;
                window.location.href = path;
              } else {
                onPickTool(id);
              }
            };
            const stateClass = isComplete ? "bg-green-50 border-green-300 text-green-800"
              : hasProj ? "bg-amber-50 border-amber-300 text-amber-900"
              : isSuggested ? "bg-white border-2 border-amber-400 ring-2 ring-amber-100 text-sage-800"
              : isLocked ? "bg-sage-50 border-sage-100 text-sage-400" : "bg-white border-sage-200 text-sage-700 hover:border-sage-400";
            return (
              <button
                key={id}
                data-testid={`journey-step-${id}`}
                onClick={onClick}
                disabled={isLocked}
                title={`${m.name}${isLocked ? " (locked — upgrade)" : hasProj ? ` (${projs.length} project${projs.length > 1 ? "s" : ""})` : " (not started)"}`}
                className={`relative flex flex-col items-center justify-start gap-1 px-2 py-2 min-w-[88px] rounded-md border transition-all ${stateClass} ${isLocked ? "cursor-not-allowed" : "cursor-pointer"}`}
              >
                <div className="relative">
                  <span className="rounded-full p-1.5" style={{ backgroundColor: hasProj ? `${m.accent}1A` : "transparent" }}>
                    <Icon className="w-4 h-4" style={{ color: isLocked ? "#94A3A0" : m.accent }} />
                  </span>
                  {isComplete && <span className="absolute -top-1 -right-1 bg-green-600 text-white rounded-full w-4 h-4 flex items-center justify-center"><Check className="w-2.5 h-2.5" /></span>}
                  {isLocked && <span className="absolute -top-1 -right-1 bg-sage-300 text-white rounded-full w-4 h-4 flex items-center justify-center"><Lock className="w-2.5 h-2.5" /></span>}
                </div>
                <p className="text-[10px] font-semibold leading-tight text-center line-clamp-2">{m.name}</p>
                {projs.length > 1 && <span className="text-[9px] bg-sage-700 text-white rounded-full px-1.5">{projs.length}</span>}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-2 text-[10px] text-sage-500 flex-wrap">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500"></span>Complete</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span>In progress</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full border-2 border-amber-400"></span>Suggested</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-white border border-sage-300"></span>Available</span>
        <span className="flex items-center gap-1"><Lock className="w-2.5 h-2.5" />Locked</span>
      </div>
    </div>
  );
}


function ProjectCard({ project, companyId, navigate }) {
  const meta = TOOL_META[project.tool_type] || { name: project.tool_type, icon: Folder, steps: 1, accent: "#7C9A7E" };
  const Icon = meta.icon;
  const step = Math.min(project.wizard_progress || 1, meta.steps);
  const pct = Math.min(100, Math.round((step / meta.steps) * 100));
  const isComplete = step >= meta.steps;
  const isStaffing = project.tool_type === "staffing_assessment";
  const path = `${isStaffing ? "/workspace" : "/tool"}/${companyId}/project/${project.id}`;
  return (
    <button
      data-testid={`project-card-${project.id}`}
      onClick={() => navigate(path)}
      className="group bg-white border border-sage-200 rounded-md text-left hover:border-sage-400 hover:shadow-md transition-all overflow-hidden flex flex-col"
      style={{ borderLeftWidth: 4, borderLeftColor: meta.accent }}
    >
      <div className="p-4 flex-1">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <span className="rounded-md p-1.5" style={{ backgroundColor: `${meta.accent}1A` }}>
              <Icon className="w-4 h-4" style={{ color: meta.accent }} />
            </span>
            <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: meta.accent }}>{meta.name}</p>
          </div>
          {isComplete ? (
            <span className="bg-green-100 text-green-700 rounded-full px-2 py-0.5 text-[10px] font-semibold flex items-center gap-1"><Check className="w-3 h-3" /> Complete</span>
          ) : (
            <span className="bg-sage-100 text-sage-700 rounded-full px-2 py-0.5 text-[10px] font-semibold">In progress</span>
          )}
        </div>
        <h3 className="font-heading text-base font-semibold text-sage-900 mb-1 group-hover:text-sage-700 line-clamp-2">{project.name}</h3>
        <p className="text-xs text-sage-500">Updated {relTime(project.updated_at)}</p>
      </div>
      <div className="px-4 pb-3">
        <div className="flex items-center justify-between text-[11px] text-sage-500 mb-1">
          <span>Step {step} / {meta.steps}</span>
          <span className="font-mono font-semibold text-sage-700">{pct}%</span>
        </div>
        <div className="h-1.5 bg-sage-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: meta.accent }} />
        </div>
      </div>
    </button>
  );
}

function NewProjectInline({ companyId, onCreated, prefillTool, onConsumePrefill }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [toolType, setToolType] = useState("staffing_assessment");
  const [busy, setBusy] = useState(false);
  const [tools, setTools] = useState([]);

  useEffect(() => {
    api.get("/tools").then(r => setTools(r.data?.tools || [])).catch(() => setTools([]));
  }, []);

  useEffect(() => {
    if (prefillTool) {
      setOpen(true);
      setToolType(prefillTool);
      setName(`${(TOOL_META[prefillTool]?.name || "Project")} for ${new Date().toLocaleDateString()}`);
      onConsumePrefill && onConsumePrefill();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillTool]);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const res = await api.post(`/companies/${companyId}/projects`, { name: name.trim(), tool_type: toolType });
      onCreated(res.data);
      setName(""); setOpen(false);
    } catch (e) {
      alert(formatError(e));
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button data-testid="new-project-button" onClick={() => setOpen(true)} className="bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 flex items-center gap-2">
        <Plus className="w-4 h-4" /> New Project
      </button>
    );
  }
  const unlocked = tools.filter(t => t.unlocked);
  const locked = tools.filter(t => !t.unlocked);
  return (
    <div className="bg-white border border-sage-200 rounded-md p-3 flex items-center gap-2 flex-wrap" data-testid="new-project-inline">
      <input data-testid="new-project-name" autoFocus placeholder="Project name" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === "Enter" && create()} className="border border-sage-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-sage-400 w-56" />
      <select data-testid="new-project-tool-type" value={toolType} onChange={e => setToolType(e.target.value)} className="border border-sage-200 rounded-md px-2 py-1.5 text-sm bg-white">
        {unlocked.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        {locked.length > 0 && <optgroup label="Locked (upgrade)">{locked.map(t => <option key={t.id} value={t.id} disabled>{t.name}</option>)}</optgroup>}
      </select>
      <button data-testid="new-project-create" onClick={create} disabled={busy || !name.trim()} className="bg-sage-600 text-white rounded-md px-3 py-1.5 text-sm font-semibold hover:bg-sage-700 disabled:opacity-50">{busy ? "Adding…" : "Add"}</button>
      <button onClick={() => setOpen(false)} className="text-sage-400 hover:text-sage-600 p-1.5"><CloseIcon className="w-4 h-4" /></button>
    </div>
  );
}

export default function CompanyOverviewPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [portalInfo, setPortalInfo] = useState(null);
  const [portalCopied, setPortalCopied] = useState(false);
  const [prefillTool, setPrefillTool] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, p, t] = await Promise.all([
          api.get(`/companies/${companyId}`),
          api.get(`/companies/${companyId}/projects`),
          api.get(`/tools`).catch(() => ({ data: { tools: [] } })),
        ]);
        setCompany(c.data);
        setProjects(p.data);
        setTools(t.data?.tools || []);
      } catch (e) { setError(formatError(e)); }
      finally { setLoading(false); }
    })();
  }, [companyId]);

  const onCreated = (proj) => {
    setProjects(ps => [proj, ...ps]);
    const path = proj.tool_type === "staffing_assessment" ? `/workspace/${companyId}/project/${proj.id}` : `/tool/${companyId}/project/${proj.id}`;
    navigate(path);
  };

  const ensurePortal = async () => {
    try {
      const r = await api.post(`/companies/${companyId}/portal-link`, {});
      setPortalInfo(r.data);
    } catch (e) { alert(formatError(e)); }
  };

  if (loading) return <div className="min-h-screen bg-cream"><Navbar /><div className="text-center pt-16 text-sage-500">Loading…</div></div>;
  if (error)   return <div className="min-h-screen bg-cream"><Navbar /><div className="max-w-lg mx-auto mt-10 bg-red-50 border border-red-200 p-5 rounded text-red-700">{error}</div></div>;

  const byTool = projects.reduce((acc, p) => { (acc[p.tool_type] = acc[p.tool_type] || []).push(p); return acc; }, {});
  const completedCount = projects.filter(p => {
    const total = TOOL_META[p.tool_type]?.steps || 1;
    return (p.wizard_progress || 1) >= total;
  }).length;

  return (
    <div className="min-h-screen bg-cream" data-testid="company-overview">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <Link to="/" className="text-xs text-sage-500 hover:text-sage-700 flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> All companies</Link>
            <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mt-1">Company Workspace</p>
            <h1 className="font-heading text-3xl font-semibold text-sage-900">{company?.name}</h1>
            <p className="text-sm text-sage-500 mt-1">{company?.industry || "—"}{company?.country ? ` · ${company.country}` : ""}{company?.local_target_pct ? ` · Target ${company.local_target_pct}%` : ""}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="portal-link-button" onClick={ensurePortal} className="flex items-center gap-1 border border-sage-200 text-sage-700 rounded-md px-3 py-2 text-sm hover:bg-sage-50"><PortalIcon className="w-4 h-4" /> Client Portal</button>
            <NewProjectInline companyId={companyId} onCreated={onCreated} prefillTool={prefillTool} onConsumePrefill={() => setPrefillTool(null)} />
          </div>
        </div>

        {/* Workflow Journey mini-map */}
        <WorkflowJourney projects={projects} tools={tools} companyId={companyId} onPickTool={(id) => setPrefillTool(id)} />

        {/* Cross-Tool Insight — surfaces connections / gaps across the toolbox */}
        <CrossToolInsightPanel projects={projects} />

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white border border-sage-200 rounded-md p-4" data-testid="stat-total-projects">
            <p className="text-xs uppercase tracking-wider font-semibold text-sage-500">Total projects</p>
            <p className="font-heading text-3xl font-bold text-sage-900">{projects.length}</p>
          </div>
          <div className="bg-white border border-sage-200 rounded-md p-4" data-testid="stat-completed">
            <p className="text-xs uppercase tracking-wider font-semibold text-sage-500">Completed</p>
            <p className="font-heading text-3xl font-bold text-green-700">{completedCount}</p>
          </div>
          <div className="bg-white border border-sage-200 rounded-md p-4" data-testid="stat-in-progress">
            <p className="text-xs uppercase tracking-wider font-semibold text-sage-500">In progress</p>
            <p className="font-heading text-3xl font-bold text-amber-700">{projects.length - completedCount}</p>
          </div>
          <div className="bg-white border border-sage-200 rounded-md p-4" data-testid="stat-tools-used">
            <p className="text-xs uppercase tracking-wider font-semibold text-sage-500">Tools used</p>
            <p className="font-heading text-3xl font-bold text-sage-900">{Object.keys(byTool).length}</p>
          </div>
        </div>

        {portalInfo && (
          <div className="bg-sage-50 border border-sage-200 rounded-md p-3 mb-6 flex items-center gap-2 flex-wrap" data-testid="portal-info">
            <PortalIcon className="w-4 h-4 text-sage-600" />
            <span className="text-xs font-semibold text-sage-700">Client Portal:</span>
            <input readOnly value={portalInfo.url} className="flex-1 min-w-[280px] border border-sage-200 rounded-md px-2 py-1 text-xs bg-white font-mono" />
            <button data-testid="portal-copy" onClick={() => { navigator.clipboard.writeText(portalInfo.url); setPortalCopied(true); setTimeout(() => setPortalCopied(false), 1500); }} className="bg-sage-600 text-white rounded-md px-3 py-1 text-xs font-semibold hover:bg-sage-700 flex items-center gap-1"><CopyIcon className="w-3 h-3" /> {portalCopied ? "Copied" : "Copy"}</button>
            <button onClick={() => setPortalInfo(null)} className="text-sage-400 hover:text-sage-600"><CloseIcon className="w-4 h-4" /></button>
          </div>
        )}

        {/* Empty state */}
        {projects.length === 0 && (
          <div className="bg-white border-2 border-dashed border-sage-200 rounded-md p-12 text-center" data-testid="empty-state">
            <Folder className="w-12 h-12 text-sage-300 mx-auto mb-3" />
            <h3 className="font-heading text-lg font-semibold text-sage-800">No projects yet</h3>
            <p className="text-sm text-sage-500 mt-1 mb-4">Pick a tool from the catalogue or create a new project to get started.</p>
            <Link to="/catalogue" className="inline-flex items-center gap-1 bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700">Browse the Lean Toolbox</Link>
          </div>
        )}

        {/* Project blocks grouped by Diagnose / Design / Sustain */}
        {projects.length > 0 && TOOL_GROUPS.map(group => {
          const list = group.ids.flatMap(id => byTool[id] || []);
          if (list.length === 0) return null;
          return (
            <section key={group.label} className="mb-8" data-testid={`group-${group.label.toLowerCase()}`}>
              <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-sage-500 mb-3">{group.label}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {list.map(p => <ProjectCard key={p.id} project={p} companyId={companyId} navigate={navigate} />)}
              </div>
            </section>
          );
        })}

        {/* Catalogue ribbon at bottom — quick discovery of unused tools */}
        {projects.length > 0 && (
          <div className="bg-white border border-sage-200 rounded-md p-4 mt-6" data-testid="suggest-more-tools">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-sage-800">Need another lens? Browse the toolbox</p>
              <Link to="/catalogue" className="text-xs text-sage-600 hover:text-sage-800 font-semibold">View all →</Link>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(TOOL_META).filter(([id]) => !byTool[id]).slice(0, 8).map(([id, m]) => {
                const Icon = m.icon;
                return (
                  <span key={id} className="inline-flex items-center gap-1 bg-sage-50 text-sage-700 rounded-full px-3 py-1 text-xs">
                    <Icon className="w-3 h-3" /> {m.name}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
